import { ArrowRight, Eye, EyeOff, Lock } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Field } from "../components/FormControls";
import { useToast } from "../components/ToastProvider";
import { clearSessionError, loginThunk } from "../features/session/sessionSlice";

function sanitizeRedirect(redirect) {
  if (!redirect || !redirect.startsWith("/")) {
    return "/";
  }

  return redirect;
}

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const { isAuthenticated, status } = useSelector((state) => state.session);
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = sanitizeRedirect(searchParams.get("redirect"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  if (isAuthenticated) {
    return <Navigate replace to={redirectTo} />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      showError("Enter your work email to continue.", { title: "Missing email" });
      return;
    }

    if (!password.trim()) {
      showError("Enter your password to continue.", { title: "Missing password" });
      return;
    }

    dispatch(clearSessionError());

    try {
      await dispatch(
        loginThunk({
          email: normalizedEmail,
          password: password.trim(),
        })
      ).unwrap();
      showSuccess("You are signed in.", { title: "Welcome back" });
      navigate(redirectTo, { replace: true });
    } catch (loginError) {
      showError(loginError || "Unable to sign in.", { title: "Sign-in failed" });
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="auth-copy">
          <p className="page-eyebrow">GrozyKart Admin</p>
          <h1 className="auth-title">Sign in</h1>
          <p className="page-description">
            Access the local admin workspace and manage catalog, orders, and operations.
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <Field
            autoComplete="email"
            label="Work email"
            placeholder="name@company.com"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            autoComplete="current-password"
            label="Password"
            placeholder="Enter your password"
            suffix={
              <button
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                aria-pressed={isPasswordVisible}
                className="input-adornment-button"
                type="button"
                onClick={() => setIsPasswordVisible((currentValue) => !currentValue)}
              >
                {isPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
            type={isPasswordVisible ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <div className="auth-actions">
            {/* <div className="auth-hint">
              <Mail size={16} />
              <span>Access and refresh tokens are stored in this browser session.</span>
            </div> */}
            <button
              className="button button-primary"
              disabled={status === "loading"}
              type="submit"
            >
              <Lock size={16} />
              <span>{status === "loading" ? "Signing in..." : "Sign in"}</span>
              <ArrowRight size={16} />
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
